import { Order } from "@/api/orders.api";
import { formatMonetaryAmount } from "@/utils/numbers.utils";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { formatOrderDateTime } from "@/utils/date.utils";
import { ExtendedListChildComponentProps } from "@/components/core/InfiniteScrollList/VirtualizedList";
import { resolveGroupPosition } from "@/components/core/InfiniteScrollList/groupPosition";
import { GroupConnector } from "@/components/core/InfiniteScrollList/GroupConnector";
import { CurrencyAmount } from "@/components/ui/CurrencyAmount";
import { TestBanner } from "@/components/core/TestBanner";
import { OrderPosDetails } from "@/components/list-cards/orders/OrderPosDetails";

type MemberOrderListCardProps = ExtendedListChildComponentProps;

export function MemberOrderListCard({
  style,
  data,
  ref,
  prevItem,
  nextItem,
}: MemberOrderListCardProps) {
  const { t } = useTranslation();

  if (!data) {
    return <div style={style} className="px-4" ref={ref} />;
  }

  const order = data as Order;
  const groupPosition = resolveGroupPosition(
    order.posGroupId,
    (prevItem as Order | undefined)?.posGroupId,
    (nextItem as Order | undefined)?.posGroupId,
  );

  return (
    <div style={style}>
      <Card
        className="hover:shadow-md transition-shadow p-0 pb-4 rounded-none border-0 border-b shadow-none relative"
        ref={ref}
      >
        <GroupConnector position={groupPosition} />

        <div className="flex items-start pl-4 justify-between">
          <div className="flex items-center gap-3">
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    "font-medium text-base",
                    "cursor-pointer hover:font-semibold transition-font-weight"
                  )}
                >
                  {order.customerName}
                </span>
                
                {order.isTestOrder && <TestBanner />}
              </div>

              <span className="text-xs text-muted-foreground">
                {formatOrderDateTime(order.paidDate)}
              </span>

              <span className="text-xs text-muted-foreground">
                {order.locationName}
              </span>
            </div>
          </div>

          <div className="text-right flex flex-col flex-shrink-0">
            <div className="font-bold text-base whitespace-nowrap tabular-nums">
              <CurrencyAmount
                amount={formatMonetaryAmount(order.amount + order.tipAmount)}
                currency={order.currency}
              />
            </div>
            
            <div className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
              <CurrencyAmount
                amount={formatMonetaryAmount(order.amount)}
                currency={order.currency}
              />{" "}
              {t("orders.bill")}
            </div>

            <div className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
              <CurrencyAmount
                amount={formatMonetaryAmount(order.tipAmount)}
                currency={order.currency}
              />{" "}
              {t("orders.tip")}
            </div>

            <div className="text-xs text-muted-foreground font-bold tabular-nums">
              #{order.simpleIdentifier}
            </div>

            <OrderPosDetails order={order} />
          </div>
        </div>
      </Card>
    </div>
  );
}
